import {
	AfterContentInit,
	Component,
	ContentChild,
	ContentChildren,
	QueryList,
	TemplateRef,
	ViewChild,
	effect,
	input,
	output,
} from '@angular/core';
import {
	NzTableComponent,
	NzTableFilterFn,
	NzTableFilterList,
	NzTableModule,
	NzTableSortFn,
	NzTableSortOrder,
} from 'ng-zorro-antd/table';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { CommonModule } from '@angular/common';
import { HeaderColumn } from './header-column';
import { RowColumn } from './row-column';
import { BeforeAfter } from '@mira-avant/mtlibs';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';

export interface Column {
	key: string;
	name: string;
	sortOrder?: NzTableSortOrder | null;
	sortFn?: NzTableSortFn<any> | null;
	filterOptions?: NzTableFilterList;
	filterFn?: NzTableFilterFn<any> | null;
	filterMultiple?: boolean;
	sortDirections?: NzTableSortOrder[];
}

@Component({
	selector: 'app-setup-wizard-dynamic-table',
	standalone: true,
	imports: [CommonModule, ZorroModule, NzTableModule, NzPopconfirmModule],
	templateUrl: './setup-wizard-dynamic-table.html',
	styleUrl: './setup-wizard-dynamic-table.scss',
})
export class SetupWizardDynamicTable<T> implements AfterContentInit {
	@ViewChild('table') table: NzTableComponent<BeforeAfter<T>> | undefined;
	selectedRow = input<BeforeAfter<T> | null>(null);
	upsertMode = input<boolean>(false);
	pageSize = input<number>(10);
	rows = input.required<BeforeAfter<T>[]>();
	columns = input<Column[]>([]);
	loading = input<boolean>(false);
	onEdit = output<object>();
	onToggleDisable = output<object>();
	onNewPage = output<BeforeAfter<T>[]>();
	onReset = output<object>();
	onRemove = output<object>();
	enableEditButton = input<boolean>(true);
	enableToggleDisableButton = input<boolean>(true);
	enableRemoveButton = input<boolean>(true);
	enableResetButton = input<boolean>(true);
	public JSON = JSON;
	@ContentChild('actions')
	actionsTemplate?: TemplateRef<{ $implicit: any }>;
	@ContentChildren(HeaderColumn) headerDirectives!: QueryList<HeaderColumn>;
	@ContentChildren(RowColumn) cellDirectives!: QueryList<RowColumn>;

	public headerMap = new Map<string, TemplateRef<any>>();
	public cellMap = new Map<string, TemplateRef<any>>();

	constructor() {
		// Watch for changes to rows() input and re-emit current page
		effect(() => {
			this.rows(); // read the signal → registers dependency
			// Only emit if the table view child is already available
			if (this.table) {
				this.emitCurrentVisibleRows();
			}
			// If table isn't ready yet → ngAfterViewInit / initial emit will catch it
		});
	}

	ngAfterContentInit(): void {
		this.headerDirectives.forEach(dir => this.headerMap.set(dir.key, dir.template));
		this.cellDirectives.forEach(dir => this.cellMap.set(dir.key, dir.template));
	}

	onEditClick(row: object) {
		this.onEdit.emit(row);
	}

	onToggleDisableClick(row: object) {
		this.onToggleDisable.emit(row);
	}

	onResetClick(row: object) {
		this.onReset.emit(row);
	}

	onRemoveClick(row: object) {
		this.onRemove.emit(row);
	}

	ngAfterViewInit() {
		// Emit initial page after view + content children are ready
		requestAnimationFrame(() => this.emitCurrentVisibleRows());
	}

	onPageIndexChange(pageIndex: number) {
		this.emitCurrentVisibleRows();
	}

	private emitCurrentVisibleRows() {
		if (!this.table) {
			throw new Error('Table reference is not available');
		}

		// nz-table with nzFrontPagination=true slices client-side automatically
		// but since we don't have direct access to the internal paginated data here,
		// easiest & most reliable way is to slice ourselves (same logic nz-table uses)
		const pageIndex = this.table.nzPageIndex || 1; // fallback to 1
		const pageSize = this.table.nzPageSize || this.pageSize();

		const start = (pageIndex - 1) * pageSize;
		const visible = this.rows().slice(start, start + pageSize);

		this.onNewPage.emit(visible);
	}
}
